/**
 * @file Live progress view rendered while the export is streaming media to
 * disk. Shows two bars (metadata / media) plus the current file name.
 */

import type { ExportProgress } from "../lib/exportRunner";

export function ExportProgressView({ progress }: { progress: ExportProgress }) {
  const metaRatio = progress.entriesTotal === 0 ? 0 : progress.entriesDone / progress.entriesTotal;
  const mediaRatio = progress.mediaTotal === 0 ? 0 : progress.mediaDone / progress.mediaTotal;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <h1 style={{ marginTop: 0 }}>エクスポート中…</h1>
      <p style={{ color: "var(--color-text-sub)", lineHeight: 1.7 }}>
        このタブを閉じないでください。閉じるとエクスポートが中断されます。
      </p>

      <ProgressBlock
        label="メタデータ"
        ratio={metaRatio}
        sub={`ひとコマ ${progress.entriesDone} / ${progress.entriesTotal} 件`}
      />
      <ProgressBlock
        label="メディア"
        ratio={mediaRatio}
        sub={`${progress.mediaDone} / ${progress.mediaTotal} 件 (${formatBytes(
          progress.mediaBytes,
        )})`}
      />
      {progress.currentMediaPath && (
        <p
          style={{
            fontSize: "12px",
            color: "var(--color-text-sub)",
            wordBreak: "break-all",
          }}
        >
          現在: {progress.currentMediaPath}
        </p>
      )}
    </section>
  );
}

function ProgressBlock({ label, ratio, sub }: { label: string; ratio: number; sub: string }) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "13px",
          marginBottom: "6px",
        }}
      >
        <span>{label}</span>
        <span style={{ color: "var(--color-text-sub)" }}>
          {Math.floor(Math.min(1, Math.max(0, ratio)) * 100)}%
        </span>
      </div>
      <div
        style={{
          height: "10px",
          borderRadius: "5px",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          overflow: "hidden",
        }}
        role="progressbar"
        aria-valuenow={Math.floor(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          style={{
            width: `${Math.min(100, Math.max(0, ratio * 100))}%`,
            height: "100%",
            background: "var(--color-accent, #4a8)",
            transition: "width 200ms",
          }}
        />
      </div>
      <p
        style={{
          fontSize: "12px",
          color: "var(--color-text-sub)",
          margin: "6px 0 0",
        }}
      >
        {sub}
      </p>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  for (const unit of units) {
    if (value < 1024) return `${value.toFixed(1)} ${unit}`;
    value /= 1024;
  }
  return `${value.toFixed(1)} PB`;
}
